// The book built from a trie of moves. The data is a tree of SAN moves from
// the standard position with names on the nodes that are openings; every
// line is replayed once with the rules the caller supplies, so that each
// node is filed under the position it reaches and a transposition finds it.
import { type Fen, type San } from '@chess/api-model';
import type { BookLine, BookReading, Opening, OpeningBook, OpeningContinuation } from './index.ts';

/** A node of the data: an opening when it has a name, with its moves. */
export interface BookNode {
  e?: string;
  n?: string;
  c?: Record<San, BookNode>;
}

export interface BookData {
  version: number;
  root: BookNode;
}

/** What replaying the lines needs from the rules of chess. */
export interface BookRules {
  start: Fen;
  /** The position after a move, or undefined if the move cannot be played. */
  play(fen: Fen, san: San): Fen | undefined;
}

/** The position without its clocks, which is what makes two FENs the same position. */
export function epdOf(fen: Fen): string {
  const fields = fen.split(' ');
  // A FEN has six fields and a board with ranks; anything else is an opaque key, as a fake may use.
  return fields.length === 6 && fields[0].includes('/') ? fields.slice(0, 4).join(' ') : fen;
}

interface Entry {
  opening?: Opening;
  /** By move; the first line to reach the position wins a move it shares. */
  branches: Map<San, OpeningContinuation>;
}

/** The nearest opening down a node: the node itself, or the shallowest named descendant. */
function nearest(node: BookNode, memo: Map<BookNode, { opening: Opening; distance: number } | undefined>) {
  if (memo.has(node)) return memo.get(node);
  let result: { opening: Opening; distance: number } | undefined;
  if (node.n) {
    result = { opening: { eco: node.e ?? '', name: node.n }, distance: 0 };
  } else if (node.c) {
    for (const child of Object.values(node.c)) {
      const below = nearest(child, memo);
      if (below && (!result || below.distance + 1 < result.distance)) {
        result = { opening: below.opening, distance: below.distance + 1 };
      }
    }
  }
  memo.set(node, result);
  return result;
}

export interface BuildOptions {
  /** Yield to the event loop after this many positions, so a big file does not freeze the page. */
  yieldEvery?: number;
}

/** Replays every line of the data and files each node under its position. */
export async function buildBook(data: BookData, rules: BookRules, options: BuildOptions = {}): Promise<OpeningBook> {
  const yieldEvery = options.yieldEvery ?? 400;
  const entries = new Map<string, Entry>();
  const memo = new Map<BookNode, { opening: Opening; distance: number } | undefined>();
  let size = 0;

  const entry = (key: string): Entry => {
    let e = entries.get(key);
    if (!e) {
      e = { branches: new Map() };
      entries.set(key, e);
    }
    return e;
  };

  // Iterative depth-first walk so the stack cannot overflow and we can pause.
  const stack: Array<{ node: BookNode; fen: Fen }> = [{ node: data.root, fen: rules.start }];
  let visited = 0;
  while (stack.length) {
    const { node, fen } = stack.pop()!;
    const key = epdOf(fen);
    const here = entry(key);
    if (node.n) {
      if (!here.opening) size++;
      here.opening ??= { eco: node.e ?? '', name: node.n };
    }
    if (node.c) {
      for (const [san, child] of Object.entries(node.c)) {
        const next = rules.play(fen, san);
        if (!next) continue;
        const towards = nearest(child, memo);
        if (towards && !here.branches.has(san)) here.branches.set(san, { san, opening: towards.opening, distance: towards.distance });
        stack.push({ node: child, fen: next });
      }
    }
    if (++visited % yieldEvery === 0) await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  const at = (fen: Fen) => entries.get(epdOf(fen));

  // The lines by name, made the first time any is asked for: the moves to
  // every named node, in the data's order, replayed into positions when a
  // name is asked for.
  let paths: Map<string, Array<{ opening: Opening; moves: San[] }>> | undefined;
  const pathsByName = () => {
    if (paths) return paths;
    const found = new Map<string, Array<{ opening: Opening; moves: San[] }>>();
    const walk: Array<{ node: BookNode; moves: San[] }> = [{ node: data.root, moves: [] }];
    // Breadth first, so each name's lines come shallow first, then in the data's order.
    for (let i = 0; i < walk.length; i++) {
      const { node, moves } = walk[i];
      if (node.n) {
        const list = found.get(node.n) ?? [];
        list.push({ opening: { eco: node.e ?? '', name: node.n }, moves });
        found.set(node.n, list);
      }
      for (const [san, child] of Object.entries(node.c ?? {})) walk.push({ node: child, moves: [...moves, san] });
    }
    paths = found;
    return found;
  };
  const replayed = new Map<string, BookLine[]>();
  const lines = (name: string): BookLine[] => {
    const cached = replayed.get(name);
    if (cached) return cached;
    const out: BookLine[] = [];
    for (const { opening, moves } of pathsByName().get(name) ?? []) {
      const positions: Fen[] = [rules.start];
      for (const san of moves) {
        const next = rules.play(positions[positions.length - 1], san);
        if (!next) break;
        positions.push(next);
      }
      if (positions.length === moves.length + 1) out.push({ opening, moves, positions });
    }
    replayed.set(name, out);
    return out;
  };
  const bySan = (a: OpeningContinuation, b: OpeningContinuation) => a.san.localeCompare(b.san);

  return {
    size,
    lookup: (fen) => at(fen)?.opening,
    knows: (fen) => {
      const e = at(fen);
      return !!e && (!!e.opening || e.branches.size > 0);
    },
    continuations: (fen) => {
      const e = at(fen);
      return e ? [...e.branches.values()].sort(bySan) : [];
    },
    lines,
    book: (positions) => {
      let named: { opening: Opening; ply: number } | undefined;
      let last: number | undefined;
      positions.forEach((fen, ply) => {
        const e = at(fen);
        if (!e) return;
        if (e.opening) named = { opening: e.opening, ply };
        if (e.opening || e.branches.size > 0) last = ply;
      });
      if (!named || last === undefined) return undefined;
      const reading: BookReading = { opening: named.opening, ply: named.ply, lastKnownPly: last };
      return reading;
    },
  };
}
